import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

/**
 * WARNING:
 * Before your make any changes to the Rovo Dev terms and conditions,
 * validate the changes with the Rovo Dev Enablement team (Jolt - #help-jolt).
 *
 * For more context, see:
 * https://hello.atlassian.net/wiki/spaces/AIDO/pages/6079320096/Rovo+Dev+Standard+sign+up+flows
 */
export const PrivacyPolicy = () => {
	if (checkFeatureFlagGate(featureFlags.rovoDevSignupFlowsCommerceDisclaimer)) {
		return (
			<div css={privacyPolicyCss}>
				<p>
					<b>
						<FormattedMessage
							id="signup-page.rovo-dev-privacy-policy.title"
							defaultMessage="Rovo Dev Standard - 30-day trial, then $20 USD per user per month"
						/>
					</b>
				</p>
				<p>
					<FormattedMessage
						id="signup-page.rovo-dev-privacy-policy.agreement-terms"
						// TODO: https://softwareteams.atlassian.net/browse/JOLT-2177
						// defaultMessage="By selecting <b>Agree and start now</b>, you agree to the <customerAgreementLink>Atlassian Customer Agreement</customerAgreementLink> and <privacyPolicyLink>Privacy Policy</privacyPolicyLink>. At the end of your trial, billing begins and renews automatically. Cancel anytime."
						defaultMessage="By selecting <b>Agree and start now</b>, you agree to the <customerAgreementLink>Atlassian Customer Agreement</customerAgreementLink> and <privacyPolicyLink>Privacy Policy</privacyPolicyLink>. At the end of your trial, billing begins and renews automatically."
						values={{
							customerAgreementLink: (chunks) => (
								<a
									css={linkCss}
									href="https://www.atlassian.com/legal/atlassian-customer-agreement"
								>
									{chunks}
								</a>
							),
							privacyPolicyLink: (chunks) => (
								<a css={linkCss} href="https://www.atlassian.com/legal/privacy-policy">
									{chunks}
								</a>
							),
							b: (chunks) => <b>{chunks}</b>,
						}}
					/>
				</p>
				<p>
					<FormattedMessage
						id="signup-page.rovo-dev-privacy-policy.mcp-information"
						defaultMessage="Rovo Dev CLI and Rovo Dev in IDE may not be available while on free trial. Rovo Dev allows users to connect and send data to Third-Party Products and data sources through model context protocol (MCP). <mcpLink>Read about MCP and your responsibilities</mcpLink>"
						values={{
							mcpLink: (chunks) => (
								<a
									css={linkCss}
									href="https://support.atlassian.com/rovo/docs/rovo-dev-and-model-context-protocol-mcp/"
								>
									{chunks}
								</a>
							),
						}}
					/>
				</p>
			</div>
		);
	}

	return (
		<div css={privacyPolicyCss}>
			<ul css={listCss}>
				<li>
					Your order contains at least one auto-renewing subscription. You’ll be charged $20 per
					user / month after your trial.
				</li>
				<li>
					By continuing you agree to pay the total amount each billing cycle, starting from the next
					bill date or after any free trials end, until you cancel. Any free trials can be canceled
					before the trial end date to avoid being charged. Your bill may change if the usage
					changes, and will reflect applicable taxes.{' '}
					<a
						css={linkCss}
						href="https://support.atlassian.com/subscriptions-and-billing/docs/cancel-a-subscription/"
					>
						Cancel
					</a>{' '}
					or{' '}
					<a
						css={linkCss}
						href="https://support.atlassian.com/subscriptions-and-billing/resources/"
					>
						change your plan
					</a>{' '}
					at any time. If prices change, we’ll notify you.
				</li>
				<li>
					Rovo Dev features allow users to connect to{' '}
					<a
						css={linkCss}
						href="https://support.atlassian.com/rovo/docs/rovo-dev-and-model-context-protocol-mcp/"
					>
						Third-Party Products
					</a>{' '}
					and data sources. You are responsible for obtaining all necessary rights and consents for
					Atlassian to process any data users submit to Rovo Dev (including data from their local
					devices and connected Third-Party Products).
				</li>
				<li>
					By selecting <b>“Agree and start now”</b>, you agree to the{' '}
					<a css={linkCss} href="https://www.atlassian.com/legal/atlassian-customer-agreement">
						Atlassian Customer Agreement
					</a>
					, and acknowledge our{' '}
					<a css={linkCss} href="https://www.atlassian.com/legal/privacy-policy">
						Privacy Policy
					</a>
					. When using Rovo Dev you must follow our{' '}
					<a css={linkCss} href="https://www.atlassian.com/legal/acceptable-use-policy">
						Acceptable Use Policy
					</a>
					.
				</li>
			</ul>
		</div>
	);
};

const privacyPolicyCss = css({
	fontSize: 13,
});

const listCss = css({
	paddingInlineStart: '15px',
});

const linkCss = css({
	color: wacToken('color.primary.blue'),
	textDecoration: 'underline',
	[':hover']: {
		textDecoration: 'none',
	},
});
